// =============================================================================
// useRideLive(onChange, { practiceId, patientId, driverId, tables, intervalMs })
//
// Ride screens refresh when someone else acts (20270114100000_ride_realtime):
// the Rides board, the driver's ride box and Ride alerts card, the ride chip
// and the patient's portal ride step. Feeds come from rideLiveSubs.
//
// One Supabase channel per set of feeds is shared by every screen that asks
// for it (a Care Schedule list can show many ride boxes), and each screen's
// onChange is debounced. A change is only a signal: screens refetch through
// their usual RPCs and queries, so RLS and auditing stay where they are. An
// interval and a refresh when the tab comes back cover a dropped socket.
// =============================================================================
import { useEffect, useRef } from "react";
import { supabase } from "../lib/supabaseClient";
import { privateChannel } from "../lib/realtime/presence";
import { patientTopic, practiceTopic } from "../lib/realtime/presenceCore";
import { rideLiveSubs } from "../lib/ridesCore";

const shared = new Map(); // key -> { ch, listeners: Set }

// scope: { practiceId } for staff screens or { patientId } for the portal;
// the private topic is checked by the realtime.messages policies.
function join(subs, fn, scope) {
  const key = subs.map((s) => s.table + "|" + s.filter).join(",");
  let entry = shared.get(key);
  if (!entry) {
    const listeners = new Set();
    const label = "ride-live-" + Math.random().toString(36).slice(2, 9);
    let ch = privateChannel(scope.practiceId ? practiceTopic(scope.practiceId, label) : patientTopic(scope.patientId, label));
    for (const s of subs) {
      ch = ch.on("postgres_changes", { event: "*", schema: "public", table: s.table, filter: s.filter }, () => {
        listeners.forEach((l) => { try { l(); } catch (e) { /* one screen failing does not stop the rest */ } });
      });
    }
    ch.subscribe();
    entry = { ch, listeners };
    shared.set(key, entry);
  }
  entry.listeners.add(fn);
  return () => {
    entry.listeners.delete(fn);
    if (entry.listeners.size === 0) {
      shared.delete(key);
      supabase.removeChannel(entry.ch);
    }
  };
}

export default function useRideLive(onChange, { practiceId = null, patientId = null, driverId = null, tables = null, intervalMs = 60000, enabled = true } = {}) {
  const cb = useRef(onChange);
  cb.current = onChange;
  const tkey = tables ? tables.join(",") : "";
  useEffect(() => {
    if (!enabled) return undefined;
    const subs = rideLiveSubs({ practiceId, patientId, driverId, tables: tkey ? tkey.split(",") : null });
    if (!subs.length) return undefined;
    let timer = null;
    const fire = () => { clearTimeout(timer); timer = setTimeout(() => { if (cb.current) cb.current(); }, 400); };
    const leave = join(subs, fire, { practiceId, patientId });
    const iv = intervalMs ? setInterval(() => { if (!document.hidden) fire(); }, intervalMs) : null;
    const onVis = () => { if (!document.hidden) fire(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearTimeout(timer);
      if (iv) clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      leave();
    };
  }, [enabled, practiceId, patientId, driverId, tkey, intervalMs]);
}
